import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogOverlay } from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { LogOut } from "lucide-react";

// Custom DialogContent without close button
const DialogContentNoClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogOverlay className="bg-black/80 backdrop-blur-sm" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}
    >
      {children}
      {/* No close button here! */}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DialogContentNoClose.displayName = "DialogContentNoClose";

const mentorApplicationSchema = z.object({
  phone: z.string().min(10, "Please enter a valid phone number"),
  bio: z.string().min(50, "Please provide more details about your experience (at least 50 characters)"),
  specialty: z.string().min(10, "Please describe your specialty (at least 10 characters)"),
  expertise: z.string().min(1, "Please specify at least one area of expertise"),
  credits: z.string().optional(),
  genres: z.array(z.string()).min(1, "Please select at least one genre"),
  hourlyRate: z.number().min(1, "Please specify your desired hourly rate"),
  yearsExperience: z.number().min(1, "Please specify your years of experience").max(50, "Please enter a realistic number of years"),
  invoiceInfo: z.string().min(10, "Please provide your invoice information for payments"),
  preferredPlatform: z.string().min(1, "Please select your preferred session platform"),
  platformDetails: z.string().optional(),
});

type MentorApplicationForm = z.infer<typeof mentorApplicationSchema>;

interface MentorApplicationModalProps {
  isOpen: boolean;
  mentorProfile: { first_name: string; last_name: string; email: string; user_id: string };
  onApplicationSubmitted: () => void;
  onSignOut: () => void;
}

export const MentorApplicationModal = ({ 
  isOpen, 
  mentorProfile, 
  onApplicationSubmitted, 
  onSignOut 
}: MentorApplicationModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<MentorApplicationForm>({
    resolver: zodResolver(mentorApplicationSchema),
    defaultValues: {
      phone: "",
      bio: "",
      specialty: "",
      expertise: "",
      genres: [],
      hourlyRate: "" as any,
      yearsExperience: "" as any,
      invoiceInfo: "",
      preferredPlatform: "google-meet",
      platformDetails: "",
    },
  });

  const onSubmit = async (data: MentorApplicationForm) => {
    setIsSubmitting(true);
    try {
      console.log('Submitting mentor application data:', data);
      
      // Ensure expertise is an array - convert string to array here
      const expertiseArray = typeof data.expertise === 'string' ? 
        data.expertise.split(',').map(s => s.trim()).filter(s => s.length > 0) : 
        (Array.isArray(data.expertise) ? data.expertise : []);

      // Process credits array
      const creditsArray = data.credits ? 
        data.credits.split(',').map(s => s.trim()).filter(s => s.length > 0) : 
        [];

      const submissionData = {
        first_name: mentorProfile.first_name,
        last_name: mentorProfile.last_name,
        email: mentorProfile.email,
        phone: data.phone,
        bio: data.bio,
        specialty: data.specialty,
        expertise: expertiseArray,
        credits: creditsArray,
        genres: data.genres,
        hourly_rate: (Number(data.hourlyRate) || 0) * 100, // Convert to cents
        years_experience: Number(data.yearsExperience) || 0,
        invoice_info: data.invoiceInfo,
        status: 'pending'
      };
      
      console.log('Final submission data:', submissionData);
      
      // Store application in database (now authenticated!)
      const { data: appData, error: dbError } = await supabase
        .from('mentor_applications')
        .insert([submissionData])
        .select()
        .single();

       if (dbError) throw dbError;

      // Send detailed mentor application email
      const { error: emailError } = await supabase.functions.invoke('send-mentor-application', {
        body: {
          firstName: mentorProfile.first_name,
          lastName: mentorProfile.last_name,
          email: mentorProfile.email,
          phone: data.phone,
          bio: data.bio,
          specialty: data.specialty,
          expertise: expertiseArray,
          hourlyRate: Number(data.hourlyRate) || 0,
          yearsExperience: Number(data.yearsExperience) || 0,
          invoiceInfo: data.invoiceInfo,
          submittedAt: new Date().toISOString(),
        },
      });

      if (emailError) {
        console.warn('Detailed email notification failed:', emailError);
      }

      // Also send admin notification
      try {
        await supabase.functions.invoke('send-admin-notification', {
          body: {
            type: 'mentor_application',
            userEmail: mentorProfile.email,
            firstName: mentorProfile.first_name,
            lastName: mentorProfile.last_name,
            additionalInfo: {
              specialty: data.specialty,
              hourlyRate: Number(data.hourlyRate) || 0,
              yearsExperience: Number(data.yearsExperience) || 0
            }
          }
        });
      } catch (notifError) {
        console.warn('Admin notification failed:', notifError);
      }

      toast.success("Application submitted successfully! We'll review it and get back to you within 5-7 business days.");
      
      // Clear form and notify parent
      form.reset();
      onApplicationSubmitted();
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(`Failed to submit application: ${error.message || 'Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} modal>
      <DialogContentNoClose 
        className="max-w-4xl max-h-[90vh] overflow-y-auto obsidian-glass border border-border/20" 
        onOpenAutoFocus={(e) => e.preventDefault()} 
        onCloseAutoFocus={(e) => e.preventDefault()} 
        onEscapeKeyDown={(e) => e.preventDefault()} 
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <Card className="border-0 bg-transparent shadow-none">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-energy">Complete Your Mentor Application</CardTitle>
            <p className="text-muted-foreground">
              Welcome {mentorProfile.first_name}! To start mentoring on Ascendry, please complete your application below.
              Your dashboard will be unlocked once approved by our team.
            </p>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number *</FormLabel>
                      <FormControl>
                        <Input placeholder="+1 (555) 123-4567" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="specialty"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Specialty *</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Music Production, Audio Engineering, Sound Design" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="hourlyRate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Desired Hourly Rate (USD) *</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            placeholder="150" 
                            value={field.value || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              field.onChange(value === "" ? "" : Number(value));
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="yearsExperience"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Years of Experience *</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            placeholder="5" 
                            value={field.value || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              field.onChange(value === "" ? "" : Number(value));
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="expertise"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Areas of Expertise *</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="e.g., Music Production, Sound Design, Audio Engineering, Mixing & Mastering, DJing, Music Business, Artist Development..."
                          value={field.value || ''}
                          onChange={(e) => {
                            // Store as string during input to preserve spaces
                            field.onChange(e.target.value);
                          }}
                          className="min-h-[80px]"
                        />
                      </FormControl>
                      <p className="text-sm text-muted-foreground">
                        List your areas of expertise separated by commas
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                 />

                 <FormField
                   control={form.control}
                   name="credits"
                   render={({ field }) => (
                     <FormItem>
                       <FormLabel>Credits & Accolades</FormLabel>
                       <FormControl>
                         <Textarea
                           placeholder="e.g., Worked with Drake, Supported by Illenium, Grammy nominated, Top 10 Billboard chart..."
                           value={field.value || ''}
                           onChange={(e) => {
                             field.onChange(e.target.value);
                           }}
                           className="min-h-[80px]"
                         />
                       </FormControl>
                       <p className="text-sm text-muted-foreground">
                         List your notable clients, achievements, and accolades separated by commas (optional)
                       </p>
                       <FormMessage />
                     </FormItem>
                   )}
                  />

                 <FormField
                   control={form.control}
                   name="genres"
                   render={({ field }) => (
                     <FormItem>
                       <FormLabel>Preferred Genres *</FormLabel>
                       <FormControl>
                         <div className="space-y-3">
                           {['EDM', 'Rock and Metal', 'Rap and Hip Hop', 'Pop', 'Indie', 'Other'].map((genre) => (
                             <div key={genre} className="flex items-center space-x-2">
                               <Checkbox
                                 id={`genre-${genre}`}
                                 checked={field.value?.includes(genre)}
                                 onCheckedChange={(checked) => {
                                   const updatedGenres = checked
                                     ? [...(field.value || []), genre]
                                     : (field.value || []).filter((g) => g !== genre);
                                   field.onChange(updatedGenres);
                                 }}
                               />
                               <Label htmlFor={`genre-${genre}`} className="font-normal cursor-pointer">
                                 {genre}
                               </Label>
                             </div>
                           ))}
                         </div>
                       </FormControl>
                       <p className="text-sm text-muted-foreground">
                         Select all genres you specialize in or are interested in mentoring
                       </p>
                       <FormMessage />
                     </FormItem>
                   )}
                  />

                <FormField
                  control={form.control}
                  name="invoiceInfo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Invoice Information *</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Enter your invoicing details (name, address, tax ID, etc.)..." 
                          className="min-h-[100px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                 />

                  <FormField
                   control={form.control}
                   name="bio"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Professional Bio *</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Describe your professional background, key achievements, and relevant work experience..." 
                          className="min-h-[120px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex flex-col sm:flex-row gap-4 pt-6">
                  <Button type="submit" disabled={isSubmitting} className="flex-1 energy-button">
                    {isSubmitting ? "Submitting..." : "Submit Application"}
                  </Button>
                  <Button type="button" variant="outline" onClick={onSignOut} className="flex-1">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out Instead
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </DialogContentNoClose>
    </Dialog>
  );
};